(() => {
  "use strict";

  const CONFIG = {
    vipVariantId: 55801084706984,
    vipSellingPlanId: 2899050664,
    vipProductHandle: "chapie-vip",
    trialDays: 3,

    subscriptionPolicyUrl:
      "https://www.mychapie.com/policies/subscription-policy",

       vipProductUrl:
      "https://www.mychapie.com/products/vip-membership-1" +
      "?selling_plan=2899050664&variant=55801084706984",

    sourceProperty: "_chapie_vip_source",
    sourceValue: "automatic_cart_offer",

    pollingDurationMs: 4000,
    pollingIntervalMs: 175,
    reconcileDelayMs: 200,
    observerCooldownMs: 300,
    postRefreshDelayMs: 450,
    maxRemoveAttempts: 5,

    debug: false
  };

  const STORAGE = {
    optedOut: "chapie_vip:opted_out",
    hadVip: "chapie_vip:had_vip"
  };

  const STATE = {
    addPromise: null,
    removePromise: null,
    reconcilePromise: null,

    reconcileQueued: false,
    ignoreObserverUntil: 0,

    productPromise: null,
    productData: null,

    toggleBusy: false,
    uiRevision: 0,

    pollingTimer: null,
    pollingStartedAt: 0,

    reconcileTimer: null,
    observerRetryTimer: null,
    postRefreshTimer: null,

    observers: []
  };

  function log(...args) {
    if (CONFIG.debug) {
      console.log("[Chapie VIP]", ...args);
    }
  }

  function warn(...args) {
    console.warn("[Chapie VIP]", ...args);
  }

  function rootUrl(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root}${path}`.replace(/\/+/g, "/");
  }

  async function jsonRequest(path, options = {}) {
    const response = await fetch(rootUrl(path), {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body
          ? { "Content-Type": "application/json" }
          : {}),
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        body || `Request failed with status ${response.status}`
      );
    }

    return response.json();
  }

  function getCart() {
    return jsonRequest("cart.js");
  }

  async function getVipProduct() {
    if (STATE.productData) {
      return STATE.productData;
    }

    if (STATE.productPromise) {
      return STATE.productPromise;
    }

    STATE.productPromise = jsonRequest(
      `products/${CONFIG.vipProductHandle}.js`
    );

    try {
      STATE.productData = await STATE.productPromise;
      return STATE.productData;
    } finally {
      STATE.productPromise = null;
    }
  }

  function isVipVariant(item) {
    return (
      Number(item?.variant_id) === Number(CONFIG.vipVariantId)
    );
  }

  /*
   * Division of labor: Appstle enforces plan/variant
   * eligibility via customer tags at checkout. This
   * script ONLY activates or deactivates the cart
   * auto-add module. It therefore manages exclusively
   * the lines it added itself — identified by its own
   * automatic_cart_offer marker property. Every VIP
   * line a customer adds manually (trial plan, paid
   * no-trial plan, anything) is invisible to this
   * script: never removed, never toggled, never
   * blocked. Eligibility for those is Appstle's job.
   */
  function isOurOfferLine(item) {
    return (
      isVipVariant(item) &&
      item?.properties?.[CONFIG.sourceProperty] ===
        CONFIG.sourceValue
    );
  }

  function hasAnyVipLine(cart) {
    return Boolean(cart?.items?.some(isVipVariant));
  }

  function isQualifyingProduct(item) {
    return (
      !isVipVariant(item) &&
      item?.gift_card !== true &&
      item?.requires_shipping !== false
    );
  }

  function hasQualifyingProduct(cart) {
    return Boolean(cart?.items?.some(isQualifyingProduct));
  }

  function findVipLine(cart) {
    return cart?.items?.find(isOurOfferLine);
  }

  function findVipLines(cart) {
    return cart?.items?.filter(isOurOfferLine) || [];
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      log("Session storage read failed:", error);
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      log("Session storage write failed:", error);
    }
  }

  function storageRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      log("Session storage removal failed:", error);
    }
  }

  function cartOptedOut() {
    return storageGet(STORAGE.optedOut) === "true";
  }

  function setCartOptOut(optedOut) {
    if (optedOut) {
      storageSet(STORAGE.optedOut, "true");
    } else {
      storageRemove(STORAGE.optedOut);
    }
  }

  function getPreviousVipState() {
    return storageGet(STORAGE.hadVip);
  }

  function setPreviousVipState(hasVip) {
    storageSet(STORAGE.hadVip, hasVip ? "true" : "false");
  }

  function isActiveVipMember() {
    return window.ChapieVipCustomer?.activeMember === true;
  }

  function isTrialUsed() {
    return window.ChapieVipCustomer?.trialUsed === true;
  }

  /*
   * The offer is blocked for active members AND for any
   * customer tagged vip-trial-used. The 3-day free trial
   * is a one-time offer: a trial-used customer must never
   * see trial messaging, never have the trial selling
   * plan (2899050664) added to their cart, and any trial
   * line found in their cart is removed. vip-trial-used
   * is the authoritative eligibility flag; cancelled-vip
   * is status-only and is intentionally not checked here.
   */
  function isOfferBlocked() {
    return isActiveVipMember() || isTrialUsed();
  }

  function getOfferInstances() {
    return Array.from(
      document.querySelectorAll("[data-chapie-vip-offer]")
    );
  }

  function getInstanceElements(container) {
    return {
      active: container.querySelector(
        "[data-chapie-vip-active]"
      ),

      inactive: container.querySelector(
        "[data-chapie-vip-inactive]"
      ),

      activeToggle: container.querySelector(
        "[data-chapie-vip-toggle-active]"
      ),

      inactiveToggle: container.querySelector(
        "[data-chapie-vip-toggle-inactive]"
      ),

      status: container.querySelector(
        "[data-chapie-vip-status]"
      ),

      title: container.querySelector(
        "[data-chapie-vip-title]"
      ),

      plan: container.querySelector(
        "[data-chapie-vip-plan]"
      ),

      currentPrice: container.querySelector(
        "[data-chapie-vip-current-price]"
      ),

      recurringPrice: container.querySelector(
        "[data-chapie-vip-recurring-price]"
      ),

      image: container.querySelector(
        "[data-chapie-vip-image]"
      ),

      imageWrap: container.querySelector(
        "[data-chapie-vip-image-wrap]"
      )
    };
  }

  function setStatus(message = "") {
    getOfferInstances().forEach(container => {
      const { status } = getInstanceElements(container);

      if (status) {
        status.textContent = message;
      }
    });
  }

  function setTogglesDisabled(disabled) {
    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

      if (elements.activeToggle) {
        elements.activeToggle.disabled = disabled;
      }

      if (elements.inactiveToggle) {
        elements.inactiveToggle.disabled = disabled;
      }
    });
  }

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    const numeric = Number(number);

    if (!Number.isFinite(numeric)) {
      return "0";
    }

    const fixed = (numeric / 100).toFixed(precision);
    const parts = fixed.split(".");

    const whole = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      `$1${thousands}`
    );

    const fraction =
      precision > 0 && parts[1]
        ? `${decimal}${parts[1]}`
        : "";

    return `${whole}${fraction}`;
  }

  function stripHtml(value) {
    return String(value || "")
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  function formatMoneyWithTemplate(cents, template) {
    if (!template || !template.includes("{{")) {
      return null;
    }

    const replacements = {
      amount: formatWithDelimiters(cents, 2, ",", "."),

      amount_no_decimals: formatWithDelimiters(
        cents,
        0,
        ",",
        "."
      ),

      amount_with_comma_separator: formatWithDelimiters(
        cents,
        2,
        ".",
        ","
      ),

      amount_no_decimals_with_comma_separator:
        formatWithDelimiters(cents, 0, ".", ","),

      amount_with_apostrophe_separator:
        formatWithDelimiters(cents, 2, "'", ".")
    };

    const result = template.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (match, key) =>
        Object.prototype.hasOwnProperty.call(
          replacements,
          key
        )
          ? replacements[key]
          : match
    );

    return stripHtml(result);
  }

  function formatMoney(cents, currencyCode) {
    const offer = getOfferInstances()[0];
    const moneyFormat = offer?.dataset?.moneyFormat || "";

    if (typeof window.Shopify?.formatMoney === "function") {
      try {
        const formatted = window.Shopify.formatMoney(
          cents,
          moneyFormat || undefined
        );

        const clean = stripHtml(formatted);

        if (clean && !clean.includes("{{")) {
          return clean;
        }
      } catch (error) {
        log("Shopify money formatting failed:", error);
      }
    }

    const templateResult = formatMoneyWithTemplate(
      cents,
      moneyFormat
    );

    if (templateResult) {
      return templateResult;
    }

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || "en-US",
        {
          style: "currency",
          currency: currencyCode || "USD"
        }
      ).format(Number(cents || 0) / 100);
    } catch {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function findVipVariant(product) {
    return product?.variants?.find(
      variant =>
        Number(variant.id) === Number(CONFIG.vipVariantId)
    );
  }

  function findProductSellingPlan(product) {
    for (const group of product?.selling_plan_groups || []) {
      const plan = group.selling_plans?.find(
        candidate =>
          Number(candidate.id) ===
          Number(CONFIG.vipSellingPlanId)
      );

      if (plan) {
        return plan;
      }
    }

    return null;
  }

  function normalizeImageValue(value) {
    if (typeof value === "string" && value.length) {
      return value;
    }

    if (value && typeof value === "object") {
      if (
        typeof value.url === "string" &&
        value.url.length
      ) {
        return value.url;
      }

      if (
        typeof value.src === "string" &&
        value.src.length
      ) {
        return value.src;
      }
    }

    return "";
  }

  function getProductImage(product, variant) {
    return (
      normalizeImageValue(variant?.featured_image) ||
      normalizeImageValue(product?.featured_image) ||
      normalizeImageValue(product?.images?.[0])
    );
  }

  function getLineImage(vipLine) {
    return (
      normalizeImageValue(vipLine?.featured_image) ||
      normalizeImageValue(vipLine?.image)
    );
  }

  function applyPriceAdjustment(basePrice, adjustment) {
    if (!adjustment) {
      return basePrice;
    }

    const value = Number(adjustment.value || 0);

    switch (adjustment.value_type) {
      case "percentage":
        return Math.round(basePrice * ((100 - value) / 100));

      case "fixed_amount":
        return Math.max(0, Math.round(basePrice - value));

      case "price":
        return Math.round(value);

      default:
        return basePrice;
    }
  }

  function getRecurringPriceCents({
    variant,
    productPlan,
    vipLine
  }) {
    const basePrice = Number(
      variant?.price ??
        vipLine?.selling_plan_allocation
          ?.compare_at_price ??
        vipLine?.original_price ??
        vipLine?.price ??
        0
    );

    const adjustments = productPlan?.price_adjustments || [];

    /*
     * Appstle implements the free trial as a price
     * adjustment. Ideally the trial adjustment carries an
     * order_count and only an adjustment with no
     * order_count applies to the ongoing recurring price.
     *
     * In practice, Appstle can report the 100%-off trial
     * adjustment WITHOUT an order_count. Applying it as
     * the ongoing price yields $0.00, which is the trial
     * cycle, not the recurring price. So a zero result is
     * rejected and the base variant price is used instead.
     *
     * This keeps the displayed recurring price parametric:
     * change the VIP variant price in Shopify and the cart
     * copy follows automatically.
     */
    const ongoingAdjustment = adjustments.find(
      adjustment => adjustment?.order_count == null
    );

    if (ongoingAdjustment) {
      const adjusted = applyPriceAdjustment(
        basePrice,
        ongoingAdjustment
      );

      if (adjusted > 0) {
        return adjusted;
      }
    }

    if (adjustments.length) {
      return basePrice;
    }

    const allocationPrice =
      vipLine?.selling_plan_allocation?.per_delivery_price;

    if (
      Number.isFinite(Number(allocationPrice)) &&
      Number(allocationPrice) > 0
    ) {
      return Number(allocationPrice);
    }

    return basePrice;
  }

  function getBillingInterval({ cartPlan, productPlan }) {
    const text = [
      cartPlan?.name,
      productPlan?.name,

      ...(cartPlan?.options || []).map(
        option => option?.value
      ),

      ...(productPlan?.options || []).map(
        option => option?.value
      )
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (text.includes("year")) {
      return "year";
    }

    if (text.includes("week")) {
      return "week";
    }

    if (text.includes("day")) {
      return "day";
    }

    return "month";
  }

  function getFallbackDisplayData(cart, vipLine) {
    const currentPriceCents =
      vipLine?.final_price ?? vipLine?.price ?? 0;

    /*
     * During the trial, the allocated line prices
     * (per_delivery_price, and sometimes original_price)
     * reflect the $0.00 trial cycle. The allocation's
     * compare_at_price holds the true base variant price,
     * so prefer it, then take the first non-zero
     * candidate. This keeps even the instant pre-fetch
     * render from ever showing $0.00/month.
     */
    const recurringCandidates = [
      vipLine?.selling_plan_allocation?.compare_at_price,
      vipLine?.original_price,
      vipLine?.selling_plan_allocation
        ?.per_delivery_price,
      vipLine?.price
    ];

    const recurringPriceCents = Number(
      recurringCandidates.find(
        value => Number(value) > 0
      ) ?? 0
    );

    return {
      title:
        vipLine?.product_title ||
        vipLine?.title ||
        "Chapie VIP",

      currentPrice: formatMoney(
        currentPriceCents,
        cart?.currency
      ),

      recurringPrice: formatMoney(
        recurringPriceCents,
        cart?.currency
      ),

      billingInterval: getBillingInterval({
        cartPlan:
          vipLine?.selling_plan_allocation?.selling_plan,

        productPlan: null
      }),

      image: getLineImage(vipLine),

      imageAlt:
        vipLine?.product_title ||
        vipLine?.title ||
        "Chapie VIP"
    };
  }

  async function getDisplayData(cart, vipLine) {
    const fallback = getFallbackDisplayData(cart, vipLine);

    try {
      const product = await getVipProduct();
      const variant = findVipVariant(product);
      const productPlan = findProductSellingPlan(product);

      const cartPlan =
        vipLine?.selling_plan_allocation?.selling_plan;

      return {
        title:
          product?.title ||
          vipLine?.product_title ||
          vipLine?.title ||
          fallback.title,

        currentPrice: fallback.currentPrice,

        recurringPrice: formatMoney(
          getRecurringPriceCents({
            variant,
            productPlan,
            vipLine
          }),
          cart?.currency
        ),

        billingInterval: getBillingInterval({
          cartPlan,
          productPlan
        }),

        image:
          getProductImage(product, variant) ||
          fallback.image,

        imageAlt:
          product?.title ||
          vipLine?.product_title ||
          fallback.imageAlt
      };
    } catch (error) {
      log("Product display fallback used:", error);
      return fallback;
    }
  }

  function applyDisplayData(data) {
    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

    if (elements.title) {
        elements.title.textContent = "";

        const titleLink = document.createElement("a");

        titleLink.href = CONFIG.vipProductUrl;
        titleLink.textContent = data.title;
        titleLink.style.color = "inherit";
        titleLink.style.textDecoration = "underline";

        elements.title.append(titleLink);
      }

      if (elements.plan) {
        elements.plan.textContent =
          "Free Shipping";
      }

      if (elements.currentPrice) {
        elements.currentPrice.textContent =
          `${data.currentPrice} today`;
      }

      if (elements.recurringPrice) {
        elements.recurringPrice.textContent = "";

        elements.recurringPrice.append(
          document.createTextNode(
            `${CONFIG.trialDays}-day free trial, then ` +
              `${data.recurringPrice}/${data.billingInterval}. `
          )
        );
      }

      if (elements.image) {
        if (data.image) {
          elements.image.src = data.image;
          elements.image.alt = data.imageAlt;
          elements.image.hidden = false;

          if (elements.imageWrap) {
            elements.imageWrap.hidden = false;
          }
        } else {
          elements.image.removeAttribute("src");
          elements.image.alt = "";
          elements.image.hidden = true;

          if (elements.imageWrap) {
            elements.imageWrap.hidden = true;
          }
        }
      }
    });
  }

  function setOfferVisibility({
    hasProduct,
    hasVip,
    activeMember
  }) {
    getOfferInstances().forEach(container => {
      const elements = getInstanceElements(container);

      container.hidden = !hasProduct || activeMember;

      if (elements.active) {
        elements.active.hidden = !hasVip || activeMember;
      }

      if (elements.inactive) {
        elements.inactive.hidden = hasVip || activeMember;
      }

      if (elements.activeToggle) {
        elements.activeToggle.checked = hasVip;

        elements.activeToggle.disabled =
          STATE.toggleBusy || activeMember;
      }

      if (elements.inactiveToggle) {
        elements.inactiveToggle.checked = false;

        elements.inactiveToggle.disabled =
          STATE.toggleBusy || activeMember;
      }

      container.dataset.vipInCart = hasVip
        ? "true"
        : "false";
    });
  }

  async function refreshOfferUi(suppliedCart = null) {
    const instances = getOfferInstances();

    if (!instances.length) {
      return;
    }

    const revision = ++STATE.uiRevision;

    const cart = suppliedCart || (await getCart());

    const hasProduct = hasQualifyingProduct(cart);
    const vipLine = findVipLine(cart);
    const hasVip = Boolean(vipLine);

    /*
     * Trial-used customers are treated exactly like
     * active members for display: the offer never
     * renders for them.
     */
    const activeMember = isOfferBlocked();

    if (activeMember || !hasProduct) {
      setOfferVisibility({
        hasProduct,
        hasVip,
        activeMember
      });

      return;
    }

    if (!vipLine) {
      /*
       * A VIP line the controller does not manage (a
       * manual purchase such as the paid no-trial plan)
       * suppresses the trial offer entirely — never
       * upsell a second membership on top of one being
       * bought.
       */
      if (hasAnyVipLine(cart)) {
        setOfferVisibility({
          hasProduct: false,
          hasVip: false,
          activeMember: false
        });

        return;
      }

      setOfferVisibility({
        hasProduct: true,
        hasVip: false,
        activeMember: false
      });

      return;
    }

    /*
     * Populate the complete module using cart.js before
     * revealing it. Product JSON then improves the data
     * without being required for display.
     */
    applyDisplayData(getFallbackDisplayData(cart, vipLine));

    setOfferVisibility({
      hasProduct: true,
      hasVip: true,
      activeMember: false
    });

    const displayData = await getDisplayData(cart, vipLine);

    if (revision !== STATE.uiRevision) {
      return;
    }

    applyDisplayData(displayData);

    setOfferVisibility({
      hasProduct: true,
      hasVip: true,
      activeMember: false
    });
  }

  async function addVipLine() {
    return jsonRequest("cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        items: [
          {
            id: Number(CONFIG.vipVariantId),
            quantity: 1,
            selling_plan: Number(CONFIG.vipSellingPlanId),
            properties: {
              [CONFIG.sourceProperty]: CONFIG.sourceValue
            }
          }
        ]
      })
    });
  }

  function isOnCartPage() {
    return (
      Boolean(
        document.querySelector(
          ".shopify-section--main-cart"
        )
      ) || /\/cart\/?$/.test(window.location.pathname)
    );
  }

  function dispatchCartRefresh(cart) {
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        detail: {
          cart,
          source: "chapie-vip"
        }
      })
    );

    /*
     * The full cart page is server-rendered and does not
     * react to cart events, so reload it to reflect the
     * changed line items.
     *
     * This only runs after an actual cart mutation. After
     * the reload the cart is already in its final state,
     * so reconcile finds nothing to change and no reload
     * loop can occur. Opt-out and had-vip flags are
     * written to sessionStorage before the mutation, so
     * they survive the reload.
     */
    if (isOnCartPage()) {
      window.location.reload();
      return;
    }

    window.clearTimeout(STATE.postRefreshTimer);

    STATE.postRefreshTimer = window.setTimeout(() => {
      bindOfferInstances();
      scheduleReconcile();
    }, CONFIG.postRefreshDelayMs);
  }

  async function ensureVip({
    force = false,
    clearOptOut = false
  } = {}) {
    /*
     * Members and trial-used customers: the auto-add
     * module is deactivated. The only cart mutation
     * permitted here is cleaning up a line THIS module
     * previously auto-added (marker match) — e.g. added
     * as a guest before logging in. Manually added VIP
     * lines are never touched; Appstle enforces their
     * eligibility.
     */
    if (isOfferBlocked()) {
      const cart = await getCart();

      if (findVipLine(cart)) {
        return removeVip({ recordOptOut: false });
      }

      setPreviousVipState(false);

      return cart;
    }

    while (STATE.addPromise) {
      const priorPromise = STATE.addPromise;

      if (!force) {
        return priorPromise;
      }

      await priorPromise.catch(() => {});

      if (STATE.addPromise === priorPromise) {
        STATE.addPromise = null;
      }
    }

    const currentPromise = (async () => {
      let cart = await getCart();

      if (clearOptOut) {
        setCartOptOut(false);
      }

      if (!hasQualifyingProduct(cart)) {
        return cart;
      }

      if (findVipLine(cart)) {
        setPreviousVipState(true);
        await refreshOfferUi(cart);
        return cart;
      }

      /*
       * A manual VIP line (e.g. the paid no-trial plan)
       * blocks the trial auto-add — one membership per
       * cart.
       */
      if (hasAnyVipLine(cart)) {
        setPreviousVipState(false);
        await refreshOfferUi(cart);
        return cart;
      }

      if (!force && cartOptedOut()) {
        await refreshOfferUi(cart);
        return cart;
      }

      await addVipLine();

      cart = await getCart();

      const added = Boolean(findVipLine(cart));

      setPreviousVipState(added);

      await refreshOfferUi(cart);

      dispatchCartRefresh(cart);

      document.dispatchEvent(
        new CustomEvent("chapie:vip-added", {
          detail: { cart }
        })
      );

      return cart;
    })();

    STATE.addPromise = currentPromise;

    try {
      return await currentPromise;
    } finally {
      if (STATE.addPromise === currentPromise) {
        STATE.addPromise = null;
      }
    }
  }

  async function removeVip({ recordOptOut = true } = {}) {
    if (STATE.removePromise) {
      return STATE.removePromise;
    }

    /*
     * Save the opt-out before removing VIP.
     *
     * This prevents an observer-triggered reconcile from
     * re-adding VIP while Shopify processes the removal.
     */
    if (recordOptOut) {
      setCartOptOut(true);
    }

    const currentPromise = (async () => {
      let cart = await getCart();

      for (
        let attempt = 0;
        attempt < CONFIG.maxRemoveAttempts;
        attempt += 1
      ) {
        const vipLines = findVipLines(cart);

        if (!vipLines.length) {
          break;
        }

        for (const vipLine of vipLines) {
          await jsonRequest("cart/change.js", {
            method: "POST",
            body: JSON.stringify({
              id: vipLine.key,
              quantity: 0
            })
          });
        }

        cart = await getCart();
      }

      if (findVipLine(cart)) {
        throw new Error(
          "Chapie VIP could not be removed after multiple attempts."
        );
      }

      setPreviousVipState(false);

      await refreshOfferUi(cart);

      dispatchCartRefresh(cart);

      document.dispatchEvent(
        new CustomEvent("chapie:vip-removed", {
          detail: { cart }
        })
      );

      return cart;
    })();

    STATE.removePromise = currentPromise;

    try {
      return await currentPromise;
    } finally {
      if (STATE.removePromise === currentPromise) {
        STATE.removePromise = null;
      }
    }
  }

  async function runReconcile() {
    const removalWasActive = Boolean(STATE.removePromise);

    if (STATE.removePromise) {
      await STATE.removePromise.catch(() => {});
    }

    let cart = await getCart();
    let hasVip = Boolean(findVipLine(cart));

    /*
     * Members and trial-used customers: module
     * deactivated. Only a line this module itself
     * auto-added (marker match) is cleaned up; manual
     * VIP lines pass through untouched for Appstle to
     * handle.
     */
    if (isOfferBlocked()) {
      if (hasVip) {
        cart = await removeVip({ recordOptOut: false });
        hasVip = false;
      }

      setPreviousVipState(false);

      await refreshOfferUi(cart);

      return cart;
    }

    const previousVipState = getPreviousVipState();

    /*
     * Native VIP removal is treated as an opt-out.
     */
    if (
      previousVipState === "true" &&
      !hasVip &&
      !removalWasActive
    ) {
      setCartOptOut(true);
    }

    /*
     * Remove an orphan VIP line when no physical
     * merchandise remains.
     */
    if (hasVip && !hasQualifyingProduct(cart)) {
      cart = await removeVip({ recordOptOut: false });
      hasVip = false;
    }

    /*
     * Add VIP for eligible nonmembers who have not
     * opted out.
     */
    if (
      hasQualifyingProduct(cart) &&
      !hasVip &&
      !hasAnyVipLine(cart) &&
      !cartOptedOut()
    ) {
      cart = await ensureVip();
      hasVip = Boolean(findVipLine(cart));
    }

    setPreviousVipState(hasVip);

    await refreshOfferUi(cart);

    return cart;
  }

  function reconcileCart() {
    if (STATE.reconcilePromise) {
      STATE.reconcileQueued = true;
      return STATE.reconcilePromise;
    }

    const currentPromise = runReconcile();

    STATE.reconcilePromise = currentPromise;

    return currentPromise.finally(() => {
      if (STATE.reconcilePromise === currentPromise) {
        STATE.reconcilePromise = null;
      }

      STATE.ignoreObserverUntil =
        Date.now() + CONFIG.observerCooldownMs;

      if (STATE.reconcileQueued) {
        STATE.reconcileQueued = false;
        scheduleReconcile();
      }
    });
  }

  async function handleToggle(toggle) {
    if (STATE.toggleBusy || isOfferBlocked()) {
      return;
    }

    STATE.toggleBusy = true;

    setTogglesDisabled(true);

    try {
      if (toggle.checked) {
        setStatus("Adding Chapie VIP…");

        await ensureVip({
          force: true,
          clearOptOut: true
        });
      } else {
        setStatus("Removing Chapie VIP…");

        await removeVip({ recordOptOut: true });
      }

      setStatus("");

      await refreshOfferUi();
    } catch (error) {
      console.error("[Chapie VIP] Toggle error:", error);

      setStatus(
        "Unable to update Chapie VIP. Please try again."
      );

      await refreshOfferUi().catch(() => {});
    } finally {
      STATE.toggleBusy = false;

      setTogglesDisabled(isOfferBlocked());
    }
  }

  function bindOfferInstances() {
    getOfferInstances().forEach(container => {
      if (container.dataset.chapieVipBound === "true") {
        return;
      }

      container.dataset.chapieVipBound = "true";

      const elements = getInstanceElements(container);

      elements.activeToggle?.addEventListener(
        "change",
        event => {
          handleToggle(event.currentTarget);
        }
      );

      elements.inactiveToggle?.addEventListener(
        "change",
        event => {
          if (!event.currentTarget.checked) {
            return;
          }

          handleToggle(event.currentTarget);
        }
      );
    });
  }

  function stopPolling() {
    if (STATE.pollingTimer) {
      window.clearTimeout(STATE.pollingTimer);
    }

    STATE.pollingTimer = null;
    STATE.pollingStartedAt = 0;
  }

  function beginProductPolling() {
    stopPolling();

    if (isOfferBlocked()) {
      scheduleReconcile();
      return;
    }

    STATE.pollingStartedAt = Date.now();

    const poll = async () => {
      try {
        const cart = await getCart();

        if (hasQualifyingProduct(cart)) {
          stopPolling();
          scheduleReconcile();
          return;
        }
      } catch (error) {
        log("Product polling error:", error);
      }

      const elapsed = Date.now() - STATE.pollingStartedAt;

      if (elapsed < CONFIG.pollingDurationMs) {
        STATE.pollingTimer = window.setTimeout(
          poll,
          CONFIG.pollingIntervalMs
        );
      } else {
        stopPolling();
      }
    };

    STATE.pollingTimer = window.setTimeout(
      poll,
      CONFIG.pollingIntervalMs
    );
  }

  function scheduleReconcile({ fromObserver = false } = {}) {
    const now = Date.now();

    if (fromObserver && now < STATE.ignoreObserverUntil) {
      const retryDelay =
        STATE.ignoreObserverUntil - now + 10;

      window.clearTimeout(STATE.observerRetryTimer);

      STATE.observerRetryTimer = window.setTimeout(() => {
        STATE.observerRetryTimer = null;

        scheduleReconcile({ fromObserver: true });
      }, retryDelay);

      return;
    }

    if (STATE.reconcilePromise) {
      STATE.reconcileQueued = true;
      return;
    }

    window.clearTimeout(STATE.reconcileTimer);

    STATE.reconcileTimer = window.setTimeout(async () => {
      STATE.reconcileTimer = null;

      bindOfferInstances();

      try {
        await reconcileCart();
      } catch (error) {
        console.error(
          "[Chapie VIP] Reconcile error:",
          error
        );
      }
    }, CONFIG.reconcileDelayMs);
  }

  document.addEventListener(
    "submit",
    event => {
      const productForm = event.target.closest(
        'form[action*="/cart/add"]'
      );

      if (!productForm) {
        return;
      }

      const variantId = Number(
        new FormData(productForm).get("id")
      );

      if (variantId === Number(CONFIG.vipVariantId)) {
        return;
      }

      beginProductPolling();
    },
    true
  );

  [
    "cart:updated",
    "cart:change",
    "cart:refresh",
    "ajaxCart:updated",
    "CartDrawer:open"
  ].forEach(eventName => {
    document.addEventListener(eventName, event => {
      if (event.detail?.source === "chapie-vip") {
        return;
      }

      beginProductPolling();
    });
  });

  function mutationIsOnlyInsideVipOffer(mutation) {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target?.parentElement;

    return Boolean(
      target?.closest?.("[data-chapie-vip-offer]")
    );
  }

  function initializeObservers() {
    STATE.observers.forEach(observer =>
      observer.disconnect()
    );

    STATE.observers = [];

    const containers = [
      document.querySelector("#cart-drawer"),
      document.querySelector(".shopify-section--main-cart")
    ].filter(Boolean);

    if (!containers.length) {
      warn(
        "Cart selectors were not found. Falling back to document.body observer."
      );

      containers.push(document.body);
    }

    containers.forEach(container => {
      const isDrawer = container.id === "cart-drawer";

      const observer = new MutationObserver(mutations => {
        const relevantMutations = mutations.filter(
          mutation =>
            !mutationIsOnlyInsideVipOffer(mutation)
        );

        if (!relevantMutations.length) {
          return;
        }

        const drawerStateChanged =
          isDrawer &&
          relevantMutations.some(
            mutation =>
              mutation.type === "attributes" &&
              ["open", "class", "aria-hidden"].includes(
                mutation.attributeName
              )
          );

        if (drawerStateChanged) {
          beginProductPolling();
        }

        scheduleReconcile({ fromObserver: true });
      });

      const observerOptions = {
        childList: true,
        subtree: true
      };

      if (isDrawer) {
        observerOptions.attributes = true;

        observerOptions.attributeFilter = [
          "open",
          "class",
          "aria-hidden"
        ];
      }

      observer.observe(container, observerOptions);

      STATE.observers.push(observer);
    });
  }

  async function initialize() {
    bindOfferInstances();
    initializeObservers();

    getVipProduct().catch(error => {
      log("Product prefetch failed:", error);
    });

    try {
      const initialCart = await getCart();

      const initialHasVip = Boolean(
        findVipLine(initialCart)
      );

      if (getPreviousVipState() === null) {
        setPreviousVipState(initialHasVip);
      }

      await reconcileCart();
    } catch (error) {
      console.error(
        "[Chapie VIP] Initialization error:",
        error
      );
    }
  }

  function boot() {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        initialize,
        { once: true }
      );
    } else {
      initialize();
    }
  }

  document.addEventListener("shopify:section:load", () => {
    bindOfferInstances();
    initializeObservers();
    scheduleReconcile();
  });

  window.addEventListener("pageshow", event => {
    if (!event.persisted) {
      return;
    }

    bindOfferInstances();
    initializeObservers();
    scheduleReconcile();
  });

  boot();

  window.ChapieVip = {
    ensure: ensureVip,
    remove: removeVip,
    reconcile: reconcileCart,
    refreshUi: refreshOfferUi,
    isActiveMember: isActiveVipMember,
    isTrialUsed: isTrialUsed
  };
})();